import { Button } from "@package/subframe/components/Button";
import { IconWithBackground } from "@package/subframe/components/IconWithBackground";
import { Loader } from "@package/subframe/components/Loader";
import { Select } from "@package/subframe/components/Select";
import { TextArea } from "@package/subframe/components/TextArea";
import {
  FeatherCheck,
  FeatherLoader,
  FeatherWifiOff,
  FeatherX,
} from "@subframe/core";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useNursery } from "@/contexts/NurseryContext";
import type { LogEntryResponseDto } from "@/generated/model";
import { listRoomsQueryControllerList } from "@/generated/rooms/rooms";
import type {
  LogEntryWithSync,
  UseOfflineLogEntriesResult,
} from "@/hooks/use-offline-log-entries";
import { memberDisplayName } from "@/lib/member-display";
import { offlineDb } from "@/lib/offline/db";

import {
  canSubmitEmergencyLighting,
  EMERGENCY_LIGHTING_INSTRUCTIONS,
  isEmergencyLightingPass,
} from "./emergency-lighting";
import { LogCheckConfirmationModal } from "./LogCheckConfirmationModal";

interface NurseryRoom {
  id: string;
  name: string;
  isActive: boolean;
}

interface EmergencyLightingViewProps {
  logTypeId: string;
  entries: UseOfflineLogEntriesResult["entries"];
  isLoading: boolean;
  isOffline: boolean;
  createEntry: UseOfflineLogEntriesResult["createEntry"];
}

export default function EmergencyLightingView({
  logTypeId,
  entries,
  isLoading: entriesLoading,
  isOffline,
  createEntry,
}: EmergencyLightingViewProps) {
  const { selectedNurseryId } = useNursery();
  const nurseryId = selectedNurseryId ?? "";
  const [selectedRoomId, setSelectedRoomId] = useState<string>("");
  const [rooms, setRooms] = useState<NurseryRoom[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [allLightsWorking, setAllLightsWorking] = useState<boolean | null>(
    null,
  );
  const [notes, setNotes] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Fetch rooms with offline cache (same pattern as SleepCheckView)
  useEffect(() => {
    if (!nurseryId) return;

    (async () => {
      const cached = await offlineDb.logEntriesCache.get(`rooms-${nurseryId}`);
      if (cached) {
        setRooms(cached.entries as unknown as NurseryRoom[]);
        setRoomsLoading(false);
      }

      if (navigator.onLine) {
        try {
          const res = await listRoomsQueryControllerList(nurseryId);
          const serverRooms = res.data as unknown as NurseryRoom[];
          setRooms(serverRooms);
          await offlineDb.logEntriesCache.put({
            id: `rooms-${nurseryId}`,
            sessionId: nurseryId,
            entries: serverRooms as unknown as unknown[],
            cachedAt: Date.now(),
          });
        } catch {
          // Keep cached data
        }
      }
      setRoomsLoading(false);
    })();
  }, [nurseryId]);

  const activeRooms = useMemo(
    () => rooms?.filter((r) => r.isActive) ?? [],
    [rooms],
  );

  const effectiveRoomId =
    selectedRoomId || (activeRooms.length > 0 ? activeRooms[0]!.id : "");

  const roomNameById = useMemo(
    () => new Map(rooms.map((r) => [r.id, r.name])),
    [rooms],
  );

  const canSubmit = canSubmitEmergencyLighting({
    roomId: effectiveRoomId,
    allLightsWorking,
  });

  const confirmAndLog = useCallback(async () => {
    if (!effectiveRoomId || allLightsWorking === null) return;
    setConfirming(true);
    try {
      await createEntry({
        logTypeId,
        data: { allLightsWorking },
        roomId: effectiveRoomId,
        notes: notes.trim() || undefined,
      });
      setConfirmOpen(false);
      setAllLightsWorking(null);
      setNotes("");
    } finally {
      setConfirming(false);
    }
  }, [effectiveRoomId, allLightsWorking, notes, logTypeId, createEntry]);

  if (roomsLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Loader size="large" />
      </div>
    );
  }

  if (activeRooms.length === 0) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-2 py-12">
        <span className="text-body font-body text-subtext-color">
          No rooms configured. Contact your manager.
        </span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Offline banner */}
      {isOffline && (
        <div className="flex w-full items-center gap-3 rounded-sm bg-warning-100 px-4 py-3">
          <IconWithBackground
            variant="warning"
            size="medium"
            icon={<FeatherWifiOff />}
            square={true}
          />
          <span className="text-body font-body text-warning-800">
            You&apos;re offline. Checks will sync when reconnected.
          </span>
        </div>
      )}

      {/* Flick test instructions */}
      <div className="flex w-full flex-col gap-2 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
        <span className="text-body-bold font-body-bold text-default-font">
          Flick test instructions
        </span>
        <ol className="flex list-decimal flex-col gap-1 pl-5">
          {EMERGENCY_LIGHTING_INSTRUCTIONS.map((step, index) => (
            <li key={index} className="text-body font-body text-subtext-color">
              {step}
            </li>
          ))}
        </ol>
      </div>

      {/* Entry form */}
      <div className="flex w-full flex-col gap-4">
        <Select
          label="Select room"
          value={effectiveRoomId}
          onValueChange={setSelectedRoomId}
        >
          {activeRooms.map((room) => (
            <Select.Item key={room.id} value={room.id}>
              {room.name}
            </Select.Item>
          ))}
        </Select>

        <div className="flex w-full flex-col gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            All emergency lights checked and working?
          </span>
          <div className="flex w-full items-center gap-2">
            <Button
              className="h-10 grow shrink-0 basis-0"
              variant={
                allLightsWorking === true
                  ? "brand-primary"
                  : "neutral-secondary"
              }
              icon={<FeatherCheck />}
              size="large"
              onClick={() => setAllLightsWorking(true)}
            >
              Yes
            </Button>
            <Button
              className="h-10 grow shrink-0 basis-0"
              variant={
                allLightsWorking === false
                  ? "destructive-primary"
                  : "neutral-secondary"
              }
              icon={<FeatherX />}
              size="large"
              onClick={() => setAllLightsWorking(false)}
            >
              No
            </Button>
          </div>
        </div>

        <div className="flex w-full flex-col gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            Comment
            <span className="text-caption font-caption text-subtext-color ml-1">
              (optional)
            </span>
          </span>
          <TextArea className="h-auto w-full flex-none" variant="outline">
            <TextArea.Input
              placeholder="Observations or failures..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </TextArea>
        </div>

        <Button
          icon={<FeatherCheck />}
          onClick={() => setConfirmOpen(true)}
          variant="brand-primary"
          size="large"
          disabled={!canSubmit}
          className="w-full"
        >
          Log check
        </Button>
      </div>

      {/* Today's checks */}
      <div className="flex w-full flex-col gap-3">
        <span className="text-body-bold font-body-bold text-default-font">
          Today&apos;s checks
        </span>

        {entriesLoading ? (
          <div className="flex w-full items-center justify-center py-8">
            <Loader size="medium" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex w-full flex-col items-center justify-center gap-1 py-8">
            <span className="text-body font-body text-subtext-color">
              No checks recorded yet
            </span>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-2">
            {entries.map((entry) => (
              <EmergencyLightingEntryRow
                key={entry.id}
                entry={entry}
                roomName={
                  entry.room?.name ||
                  (entry.room?.id
                    ? (roomNameById.get(entry.room.id) ?? "")
                    : "")
                }
              />
            ))}
          </div>
        )}
      </div>

      <LogCheckConfirmationModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Confirm flick test"
        confirmationText={EMERGENCY_LIGHTING_INSTRUCTIONS}
        onConfirm={confirmAndLog}
        isSubmitting={confirming}
      />
    </div>
  );
}

function EmergencyLightingEntryRow({
  entry,
  roomName,
}: {
  entry: LogEntryResponseDto | LogEntryWithSync;
  roomName: string;
}) {
  const passed = isEmergencyLightingPass(entry.data as Record<string, unknown>);
  const isPending =
    "localSyncState" in entry && entry.localSyncState === "pending";
  const recordedByName = memberDisplayName(entry.recordedBy);

  return (
    <div
      className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 ${
        passed ? "border-neutral-200 bg-neutral-50" : "border-red-200 bg-red-50"
      }`}
    >
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          passed ? "bg-green-100" : "bg-red-100"
        }`}
      >
        {passed ? (
          <FeatherCheck className="h-4 w-4 text-green-600" />
        ) : (
          <FeatherX className="h-4 w-4 text-red-600" />
        )}
      </div>
      <div className="flex grow flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            {format(new Date(entry.timestamp), "HH:mm")}
          </span>
          <span
            className={`text-caption-bold font-caption-bold ${passed ? "text-green-600" : "text-red-600"}`}
          >
            {passed ? "Pass" : "Fail"}
          </span>
          {isPending && (
            <FeatherLoader className="h-3 w-3 animate-spin text-subtext-color" />
          )}
        </div>
        <span className="text-caption font-caption text-subtext-color">
          {roomName}
        </span>
        {entry.notes && (
          <span className="text-caption font-caption text-subtext-color italic">
            {entry.notes}
          </span>
        )}
      </div>
      <span className="text-caption font-caption text-subtext-color">
        {recordedByName}
      </span>
    </div>
  );
}
